import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, UserPlus, Search, Filter, MoreVertical, Mail,
  Shield, Crown, Eye, Edit3, Trash2, RefreshCw, X,
  ChevronDown, Check, AlertCircle, Copy, UserCheck,
  UserX, ArrowRightLeft, Clock, AlertTriangle, Calculator, Database, Scale, Handshake
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { isSeatRoleSotEnabled } from '../config/seatRoles';
import { useAuth } from '../contexts/TestAuthContext';
import { useActivityLog } from '../hooks/useActivityLog';
import { api } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
  role_display: string;
  role_color: string;
  status: string;
  created_at: string;
  last_active: string | null;
  last_login: string | null;
  invited_by: string | null;
}

interface TeamStats {
  total_members: number;
  active_members: number;
  pending_invites: number;
  by_role: Record<string, number>;
}

interface RoleInfo {
  value: string;
  label: string;
  description: string;
  color: string;
  permissions: string[];
}

const ROLE_ICONS: Record<string, React.ReactNode> = {
  owner: <Crown className="w-4 h-4" />,
  admin: <Shield className="w-4 h-4" />,
  analyst: <Edit3 className="w-4 h-4" />,
  viewer: <Eye className="w-4 h-4" />
};

// Per-seat logins Phase 7 (REACT_APP_SEAT_ROLE_SOT on): icons for the Phase 4 role vocabulary.
const SEAT_ROLE_ICONS: Record<string, React.ReactNode> = {
  accountant: <Calculator className="w-4 h-4" />,
  data_specialist: <Database className="w-4 h-4" />,
  attorney: <Scale className="w-4 h-4" />,
  mediator: <Handshake className="w-4 h-4" />
};

export default function TeamManagement() {
  const seatRoleSot = isSeatRoleSotEnabled();

  function roleBadgeColor(role: string): string {
    const r = (role || '').toLowerCase();
    if (r === 'owner') return 'var(--color-text-primary)';
    if (['admin', 'analyst', 'accountant', 'data_specialist', 'accountant_internal'].includes(r)) return 'var(--color-text-secondary)';
    return 'var(--color-text-muted)';
  }
  const { getToken } = useAuth();
  const { logActivity } = useActivityLog();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [stats, setStats] = useState<TeamStats | null>(null);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [myPermissions, setMyPermissions] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDirectAddModal, setShowDirectAddModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState<TeamMember | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState<TeamMember | null>(null);
  const [showStatusModal, setShowStatusModal] = useState<TeamMember | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{email: string; temporary_password: string} | null>(null);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [membersData, statsData, rolesData, permsData] = await Promise.all([
        api.get('/api/v1/team/members', { headers, showErrorToast: false }),
        api.get('/api/v1/team/stats', { headers, showErrorToast: false }),
        api.get('/api/v1/team/roles', { headers, showErrorToast: false }),
        api.get('/api/v1/team/my-permissions', { headers, showErrorToast: false })
      ]);
      
      setMembers(membersData);
      setStats(statsData);
      setRoles(rolesData.roles || []);
      setMyPermissions(permsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtered members
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      const matchesSearch = !searchQuery || 
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.name && member.name.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesRole = roleFilter === 'all' || member.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
      
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [members, searchQuery, roleFilter, statusFilter]);

  // Invite member
  const handleInvite = async (email: string, role: string, name: string, sendEmail: boolean) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.post('/api/v1/team/invite', 
        { email, role, name, send_email: sendEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Log invite activity
      logActivity({
        actionType: 'invite',
        actionDetails: `Invited ${email} as ${role}`,
        section: 'team_management',
        metadata: { invited_email: email, role, name, email_sent: sendEmail }
      });
      
      setActionSuccess('Invitation sent successfully!');
      setShowInviteModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Direct add member
  const handleDirectAdd = async (email: string, name: string, role: string) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      const data = await api.post('/api/v1/team/add-direct',
        { email, name, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCredentials(data.credentials);
      
      // Log add member activity
      logActivity({
        actionType: 'add_member',
        actionDetails: `Added ${email} as ${role}`,
        section: 'team_management',
        metadata: { added_email: email, role, name }
      });
      
      setActionSuccess('Member added successfully!');
      setShowDirectAddModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Change role
  const handleChangeRole = async (memberId: string, newRole: string) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.put(`/api/v1/team/members/${memberId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActionSuccess('Role updated successfully!');
      setShowRoleModal(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Remove member
  const handleRemove = async (memberId: string) => {
    const memberToRemove = members.find(m => m.id === memberId);
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.delete(`/api/v1/team/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Log remove member activity
      logActivity({
        actionType: 'remove_member',
        actionDetails: `Removed ${memberToRemove?.email || memberId} from team`,
        section: 'team_management',
        metadata: { removed_email: memberToRemove?.email, removed_name: memberToRemove?.name }
      });
      
      setActionSuccess('Member removed successfully!');
      setShowRemoveModal(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Update member status (enable/disable)
  const handleStatusChange = async (memberId: string, newStatus: string) => {
    const memberToUpdate = members.find(m => m.id === memberId);
    try {
      setActionLoading(true);
      const token = await getToken();
      
      await api.put(`/api/v1/team/members/${memberId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const statusLabel = newStatus === 'disabled' ? 'disabled' : 'enabled';
      
      // Log status change activity
      logActivity({
        actionType: 'update_role',
        actionDetails: `${statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)} ${memberToUpdate?.email || memberId}`,
        section: 'team_management',
        metadata: { member_email: memberToUpdate?.email, new_status: newStatus }
      });
      
      setActionSuccess(`Member ${statusLabel} successfully!`);
      setShowStatusModal(null);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Transfer ownership
  const handleTransferOwnership = async (newOwnerId: string, newRoleForCurrent: string) => {
    try {
      setActionLoading(true);
      const token = await getToken();
      
      const data = await api.post('/api/v1/team/transfer-ownership',
        { new_owner_id: newOwnerId, new_role_for_current: newRoleForCurrent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setActionSuccess(`Ownership transferred to ${data.new_owner_email}. Your new role is ${data.your_new_role}.`);
      setShowTransferModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Clear notifications
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => setActionSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>Team Members</h2>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Manage your organization's team members and permissions</p>
        </div>
        {myPermissions?.can_invite && (
          <div className="flex gap-3">
            {seatRoleSot ? (
              <Link
                to="/admin/users"
                className="px-3 py-2 rounded-lg flex items-center gap-2 font-medium text-sm"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <UserPlus className="w-4 h-4" />
                Provision seats
              </Link>
            ) : (
            <>
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-3 py-2 rounded-lg flex items-center gap-2 font-medium text-sm"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Mail className="w-4 h-4" />
              Invite by Email
            </button>
            <button
              onClick={() => setShowDirectAddModal(true)}
              className="px-3 py-2 rounded-lg flex items-center gap-2 font-medium text-sm"
              style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
            >
              <UserPlus className="w-4 h-4" />
              Add Directly
            </button>
            </>
            )}
            {myPermissions?.role === 'owner' && filteredMembers.filter(m => m.status === 'active' && m.role !== 'owner').length > 0 && (
              <button
                onClick={() => setShowTransferModal(true)}
                className="px-3 py-2 rounded-lg flex items-center gap-2 font-medium text-sm"
                style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}
              >
                <ArrowRightLeft className="w-4 h-4" />
                Transfer Ownership
              </button>
            )}
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {actionSuccess && (
        <div className="border rounded-lg p-4 flex items-center gap-3" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
          <Check className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
          <span style={{ color: 'var(--color-verified)' }}>{actionSuccess}</span>
        </div>
      )}

      {error && (
        <div className="border rounded-lg p-4 flex items-center justify-between" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            <span style={{ color: 'var(--color-needs-human-text)' }}>{error}</span>
          </div>
          <button onClick={() => setError(null)} style={{ color: 'var(--color-needs-human-text)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Credentials Display (after direct add) */}
      {credentials && (
        <div className="border rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <h4 className="font-semibold mb-2" style={{ color: 'var(--color-needs-human-text)' }}>New Member Credentials</h4>
          <p className="text-sm mb-3" style={{ color: 'var(--color-needs-human-text)' }}>Share these credentials securely with the new team member:</p>
          <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--color-text-secondary)' }}>Email:</span>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>{credentials.email}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(credentials.email)}
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span style={{ color: 'var(--color-text-secondary)' }}>Temporary Password:</span>
              <div className="flex items-center gap-2">
                <code className="px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>{credentials.temporary_password}</code>
                <button
                  onClick={() => navigator.clipboard.writeText(credentials.temporary_password)}
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <button
            onClick={() => setCredentials(null)}
            className="mt-3 text-sm"
            style={{ color: 'var(--color-needs-human-text)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Total Members</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stats.total_members}</p>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Active</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>{stats.active_members}</p>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Pending Invites</p>
            <p className="text-2xl font-bold" style={{ color: 'var(--color-brand)' }}>{stats.pending_invites}</p>
          </div>
          <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <p className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Your Role</p>
            <p className="text-lg font-bold" style={{ color: seatRoleSot ? roleBadgeColor(myPermissions?.role) : myPermissions?.role_color }}>
              {myPermissions?.role_display}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg p-4 border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              />
            </div>
          </div>

          {/* Role Filter */}
          <div className="relative">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="all">All Roles</option>
              {seatRoleSot ? (
                roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)
              ) : (
                <>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
                </>
              )}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-dim)' }} />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-3 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="disabled">Disabled</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-dim)' }} />
          </div>

          {/* Refresh */}
          <button
            onClick={fetchData}
            className="px-3 py-2 border rounded-lg flex items-center gap-2 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Members Table */}
      <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border)' }}>
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>Member</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>Last Login</th>
                <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>Joined</th>
                <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-label)' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--color-border-faint)]">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    {searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
                      ? 'No members match your filters'
                      : 'No team members yet. Invite your first team member!'}
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className={`${member.status === 'disabled' ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                          style={
                            member.status === 'disabled'
                              ? { backgroundColor: 'var(--color-text-dim)', color: 'var(--color-on-brand)' }
                              : { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                          }
                        >
                          {(member.name || member.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{member.name || 'No name'}</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {seatRoleSot ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap"
                          style={{
                            backgroundColor: 'transparent',
                            color: roleBadgeColor(member.role),
                            borderColor: roleBadgeColor(member.role),
                          }}
                        >
                          {ROLE_ICONS[member.role] ?? SEAT_ROLE_ICONS[member.role]}
                          {member.role_display}
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${member.role_color}15`,
                            color: member.role_color
                          }}
                        >
                          {ROLE_ICONS[member.role] ?? undefined}
                          {member.role_display}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={
                          member.status === 'active' ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                          member.status === 'pending' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' } :
                          { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        }
                      >
                        {member.status === 'active' && <UserCheck className="w-3 h-3" />}
                        {member.status === 'pending' && <Mail className="w-3 h-3" />}
                        {member.status === 'disabled' && <UserX className="w-3 h-3" />}
                        {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {member.last_login ? (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" style={{ color: 'var(--color-text-dim)' }} />
                          {formatRelativeTime(member.last_login)}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--color-text-dim)' }}>Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {myPermissions?.can_change_roles && member.role !== 'owner' && member.status !== 'pending' && (
                          <button
                            onClick={() => setShowRoleModal(member)}
                            className="p-1.5 rounded"
                            style={{ color: 'var(--color-text-dim)' }}
                            title="Change role"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        {myPermissions?.can_remove && member.role !== 'owner' && member.status === 'active' && (
                          <button
                            onClick={() => setShowStatusModal(member)}
                            className="p-1.5 rounded"
                            style={{ color: 'var(--color-text-dim)' }}
                            title="Disable member"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}
                        {myPermissions?.can_remove && member.role !== 'owner' && member.status === 'disabled' && (
                          <button
                            onClick={() => handleStatusChange(member.id, 'active')}
                            className="p-1.5 rounded"
                            style={{ color: 'var(--color-text-dim)' }}
                            title="Enable member"
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}
                        {myPermissions?.can_remove && member.role !== 'owner' && (
                          <button
                            onClick={() => setShowRemoveModal(member)}
                            className="p-1.5 rounded"
                            style={{ color: 'var(--color-text-dim)' }}
                            title="Remove member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination info */}
        <div className="px-4 py-3 border-t" style={{ backgroundColor: 'var(--color-bg-base)', borderColor: 'var(--color-border-faint)' }}>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Showing {filteredMembers.length} of {members.length} members
          </p>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteModal
          roles={roles}
          onClose={() => setShowInviteModal(false)}
          onInvite={handleInvite}
          loading={actionLoading}
        />
      )}

      {/* Direct Add Modal */}
      {showDirectAddModal && (
        <DirectAddModal
          roles={roles}
          onClose={() => setShowDirectAddModal(false)}
          onAdd={handleDirectAdd}
          loading={actionLoading}
        />
      )}

      {/* Change Role Modal */}
      {showRoleModal && (
        <ChangeRoleModal
          member={showRoleModal}
          roles={roles}
          onClose={() => setShowRoleModal(null)}
          onChangeRole={handleChangeRole}
          loading={actionLoading}
        />
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <RemoveModal
          member={showRemoveModal}
          onClose={() => setShowRemoveModal(null)}
          onRemove={handleRemove}
          loading={actionLoading}
        />
      )}

      {/* Disable/Enable Status Modal */}
      {showStatusModal && (
        <StatusModal
          member={showStatusModal}
          onClose={() => setShowStatusModal(null)}
          onChangeStatus={handleStatusChange}
          loading={actionLoading}
        />
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <TransferOwnershipModal
          members={filteredMembers.filter(m => m.status === 'active' && m.role !== 'owner')}
          roles={roles}
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransferOwnership}
          loading={actionLoading}
        />
      )}
    </div>
  );
}


// ============== Modal Components ==============

function InviteModal({ roles, onClose, onInvite, loading }: {
  roles: RoleInfo[];
  onClose: () => void;
  onInvite: (email: string, role: string, name: string, sendEmail: boolean) => void;
  loading: boolean;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('analyst');
  const [sendEmail, setSendEmail] = useState(true);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Invite Team Member</h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              placeholder="colleague@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              {roles.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {roles.find(r => r.value === role) && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                {roles.find(r => r.value === role)?.description}
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="w-4 h-4 rounded"
              style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
            />
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Send invitation email</span>
          </label>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onInvite(email, role, name, sendEmail)}
            disabled={!email || loading}
            className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
}

function DirectAddModal({ roles, onClose, onAdd, loading }: {
  roles: RoleInfo[];
  onClose: () => void;
  onAdd: (email: string, name: string, role: string) => void;
  loading: boolean;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('analyst');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Add Team Member Directly</h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="border rounded-lg p-3 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
            This will create an account and generate temporary credentials that you&apos;ll need to share with the user.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              placeholder="John Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
              placeholder="john@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              {roles.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onAdd(email, name, role)}
            disabled={!email || !name || loading}
            className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Add Member
          </button>
        </div>
      </div>
    </div>
  );
}

function ChangeRoleModal({ member, roles, onClose, onChangeRole, loading }: {
  member: TeamMember;
  roles: RoleInfo[];
  onClose: () => void;
  onChangeRole: (memberId: string, role: string) => void;
  loading: boolean;
}) {
  const [newRole, setNewRole] = useState(member.role);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Change Role</h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Change role for <strong>{member.name || member.email}</strong>
          </p>
        </div>

        <div className="space-y-2">
          {roles.map(r => (
            <label
              key={r.value}
              className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition"
              style={
                newRole === r.value
                  ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)' }
                  : { borderColor: 'var(--color-border)' }
              }
            >
              <input
                type="radio"
                name="role"
                value={r.value}
                checked={newRole === r.value}
                onChange={(e) => setNewRole(e.target.value)}
                className="sr-only"
              />
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${r.color}20`, color: r.color }}>
                {ROLE_ICONS[r.value]}
              </div>
              <div className="flex-1">
                <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{r.label}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{r.description}</p>
              </div>
              {newRole === r.value && (
                <Check className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              )}
            </label>
          ))}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onChangeRole(member.id, newRole)}
            disabled={newRole === member.role || loading}
            className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Update Role
          </button>
        </div>
      </div>
    </div>
  );
}

function RemoveModal({ member, onClose, onRemove, loading }: {
  member: TeamMember;
  onClose: () => void;
  onRemove: (memberId: string) => void;
  loading: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Remove Team Member</h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p style={{ color: 'var(--color-needs-human-text)' }}>
            Are you sure you want to remove <strong>{member.name || member.email}</strong> from the team?
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
            They will lose access to all claims and reports.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onRemove(member.id)}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Remove Member
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusModal({ member, onClose, onChangeStatus, loading }: {
  member: TeamMember;
  onClose: () => void;
  onChangeStatus: (memberId: string, status: string) => void;
  loading: boolean;
}) {
  const isDisabling = member.status === 'active';
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {isDisabling ? 'Disable Team Member' : 'Enable Team Member'}
          </h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          className="border rounded-lg p-4 mb-4"
          style={
            isDisabling
              ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
              : { backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }
          }
        >
          <p style={{ color: isDisabling ? 'var(--color-brand)' : 'var(--color-verified)' }}>
            {isDisabling
              ? <>Are you sure you want to disable <strong>{member.name || member.email}</strong>?</>
              : <>Re-enable access for <strong>{member.name || member.email}</strong>?</>
            }
          </p>
          <p className="text-sm mt-2" style={{ color: isDisabling ? 'var(--color-brand)' : 'var(--color-verified)' }}>
            {isDisabling
              ? 'They will not be able to log in until re-enabled. Their data will be preserved.'
              : 'They will regain access to the team with their previous role.'
            }
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onChangeStatus(member.id, isDisabling ? 'disabled' : 'active')}
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={
              isDisabling
                ? { backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                : { backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
            }
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : (isDisabling ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />)}
            {isDisabling ? 'Disable Member' : 'Enable Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TransferOwnershipModal({ members, roles, onClose, onTransfer, loading }: {
  members: TeamMember[];
  roles: RoleInfo[];
  onClose: () => void;
  onTransfer: (newOwnerId: string, newRoleForCurrent: string) => void;
  loading: boolean;
}) {
  const [selectedMember, setSelectedMember] = useState<string>('');
  const [newRoleForCurrent, setNewRoleForCurrent] = useState('admin');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Transfer Ownership</h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <p className="font-medium flex items-center gap-1.5" style={{ color: 'var(--color-brand)' }}><AlertTriangle size={14} /> This action cannot be undone by you</p>
          <p className="text-sm mt-1" style={{ color: 'var(--color-brand)' }}>
            The new owner will have full control over the team and can change your role.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Select New Owner</label>
            <select
              value={selectedMember}
              onChange={(e) => setSelectedMember(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="">Choose a team member...</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name || m.email} ({m.role_display})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Your New Role After Transfer</label>
            <select
              value={newRoleForCurrent}
              onChange={(e) => setNewRoleForCurrent(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
            >
              <option value="admin">Admin</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              You will become a {newRoleForCurrent} after transferring ownership.
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border rounded-lg"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onTransfer(selectedMember, newRoleForCurrent)}
            disabled={!selectedMember || loading}
            className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
            Transfer Ownership
          </button>
        </div>
      </div>
    </div>
  );
}

